import { useCallback, useEffect, useRef, useMemo } from 'react';
import classNames from 'classnames';
import { scaleLinear } from 'd3-scale';
import { select, selectAll } from 'd3-selection';
import { line } from 'd3-shape';
import { isNil, isFinite } from 'lodash';
import { useUnmount } from 'react-use';
import { FEET } from '@surfline/web-common';

import type { SurfData, SurfTimestep, ObservationType } from 'types/surf';
import type { ConditionRating } from 'types/conditionRating';
import type { SurfHeight } from 'types/units';
import { roundSurfHeight } from 'utils/roundUnits';
import { getGraphYScale, getMaxWaveHeight, scaleWaveHeight } from 'utils/scaleWaveHeight';
import { isCurrentHour } from 'utils/time';

import styles from './useSetupSurfGraphCanvas.module.scss';

const getBarClassNames = ({
  isActive,
  isObservedActive,
  isActiveBarObserved,
}: {
  isActive: boolean;
  isObservedActive: boolean;
  isActiveBarObserved?: boolean;
}) =>
  classNames({
    [styles.bar]: true,
    [styles['bar--active']]: isActive && !isActiveBarObserved,
    [styles['bar--observed']]: (!isActive && isObservedActive) || (isActive && isActiveBarObserved),
  });

const getVerticalLineClassNames = ({
  isActive,
  isCurrentTimeHour,
}: {
  isActive: boolean;
  isCurrentTimeHour: boolean;
}) =>
  classNames(styles.verticalLine, 'vertical-line', {
    [styles['verticalLine--active']]: isActive,
    [styles['verticalLine--currentHour']]: isCurrentTimeHour,
  });

interface Props {
  activeCondition?: ConditionRating;
  activeHour?: number;
  data: SurfData;
  day: number;
  dayIndex?: number;
  graphHeight?: number;
  graphWidth?: number;
  isMobile: boolean;
}

const useSetupSurfGraphCanvas = ({
  activeHour,
  data,
  day,
  dayIndex,
  graphHeight,
  graphWidth,
  isMobile = false,
}: Props) => {
  const graphInitializedRef = useRef(false);

  const isActiveHourObserved = useMemo(() => {
    if (activeHour === undefined || day !== 0 || !data.observations) return false;
    return !!data.observations[activeHour]?.surf?.max;
  }, [activeHour, day, data.observations]);

  const updateActiveBar = useCallback(() => {
    // First reset all active styles regardless of whether activeHour is defined
    // Scope all selectors to the current chart instance
    selectAll(`#surf-graph-canvas-day-${day} .${styles['barLabel--selected']}`).attr('class', () =>
      classNames(styles.barLabel),
    );
    selectAll(`#surf-graph-canvas-day-${day} .${styles['bar--active']}`).attr('class', () =>
      getBarClassNames({
        isActive: false,
        isObservedActive: isActiveHourObserved,
        isActiveBarObserved: false,
      }),
    );
    selectAll(`#surf-graph-canvas-day-${day} .${styles['bar--observed']}`).attr('class', () =>
      getBarClassNames({
        isActive: false,
        isObservedActive: isActiveHourObserved,
        isActiveBarObserved: false,
      }),
    );

    // Reset vertical line styles
    selectAll(`#surf-graph-canvas-day-${day} .${styles['verticalLine--active']}`).attr(
      'class',
      (_, i) => {
        // Check if this is the current hour
        const isCurrentTimeHour =
          day === 0 &&
          data.observations &&
          i < data.observations.length &&
          isCurrentHour(data.observations[i].timestamp);

        return getVerticalLineClassNames({
          isActive: false,
          isCurrentTimeHour: !!isCurrentTimeHour,
        });
      },
    );

    // Only apply active styles if activeHour is defined
    if (isFinite(activeHour)) {
      // add active styles to new active bar (already properly scoped)
      selectAll(`#surf-graph-canvas-day-${day} .${styles.bar}`).attr('class', (_, i) => {
        const isThisBarActive = i === activeHour;
        const isThisBarObserved = isThisBarActive && isActiveHourObserved;

        return getBarClassNames({
          isActive: isThisBarActive,
          isObservedActive: isActiveHourObserved,
          isActiveBarObserved: isThisBarObserved,
        });
      });

      // add active styles to vertical lines
      selectAll(`#surf-graph-canvas-day-${day} .${styles.verticalLine}`).attr('class', (_, i) => {
        // Check if this is the current hour
        const isCurrentTimeHour =
          day === 0 &&
          data.observations &&
          i < data.observations.length &&
          isCurrentHour(data.observations[i].timestamp);

        return getVerticalLineClassNames({
          isActive: i === activeHour,
          isCurrentTimeHour: !!isCurrentTimeHour,
        });
      });

      // add active styles to y axis labels (already properly scoped)
      selectAll(`#surf-graph-canvas-day-${day} .${styles.barLabel}`).attr('class', (_, i) =>
        classNames(styles.barLabel, { [styles['barLabel--selected']]: i === activeHour }),
      );
    }
  }, [activeHour, day, data.observations, isActiveHourObserved]);

  useEffect(() => {
    if (graphInitializedRef.current) {
      updateActiveBar();
    }
  }, [updateActiveBar]);

  const setupGraph = useCallback(() => {
    if (!!graphHeight && !!graphWidth && isFinite(graphHeight) && isFinite(graphWidth)) {
      const GRAPH_WIDTH = graphWidth;
      const GRAPH_HEIGHT = graphHeight;
      const GRAPH_LABEL_HEIGHT = 20;
      const CORNER_RADIUS = 3;

      const graphData = data?.days?.[day || 0];

      if (isNil(graphData)) {
        return;
      }

      // calculate even distribution of the groups
      const numBars = graphData?.length ?? 1;
      const groupWidth = GRAPH_WIDTH / numBars;
      // calculate the width of the bars to position with the groups
      const resize = scaleLinear().domain([2, 16]).range([1, 10]);
      const barWidth = resize(groupWidth);
      const barPositionX = groupWidth / 2 - barWidth / 2;

      // Note: we use scaleLinear instead of scaleTime to handle DST shifts and our time data is always linear
      const xScale = scaleLinear()
        .range([0, GRAPH_WIDTH - groupWidth])
        .domain([0, numBars - 1]);

      const maxTotalHeight =
        data?.overallMaxSurfHeight ??
        Math.max(
          ...(data?.days?.flatMap((singleDay) =>
            singleDay.map((dayItem) => dayItem.surf.raw.max),
          ) ?? []),
        );
      const units = (data?.units?.waveHeight ?? FEET) as SurfHeight;
      const yAxisScaleType = getGraphYScale(maxTotalHeight, units);

      const yScale = scaleLinear()
        .range([0, GRAPH_HEIGHT - GRAPH_LABEL_HEIGHT])
        .domain([0, getMaxWaveHeight(maxTotalHeight, units)]);

      const barHeight = (reading: SurfTimestep) => {
        const minWaveHeightThreshold = data?.units?.waveHeight?.toLowerCase() === 'm' ? 0.12 : 0.4;
        const maxHeight =
          reading.surf.raw.max <= minWaveHeightThreshold
            ? minWaveHeightThreshold
            : reading.surf.raw.max;

        return Math.abs(
          yAxisScaleType === 'exponent'
            ? scaleWaveHeight(
                maxHeight,
                data?.units?.waveHeight as SurfHeight,
                // account for top labels on bars
                GRAPH_HEIGHT - GRAPH_LABEL_HEIGHT,
              )
            : yScale(maxHeight),
        );
      };

      const barYPosition = (reading: SurfTimestep) =>
        GRAPH_HEIGHT - barHeight(reading) + CORNER_RADIUS;

      // Select existing canvas or create a new one
      const container = select<HTMLElement, unknown>(`#surf-graph-canvas-day-${day}`);
      let canvas = container.select<SVGSVGElement>(`svg.forecastGraphSurf-${day}`);

      // Create SVG if it doesn't exist
      if (canvas.empty()) {
        // Remove any leftover SVGs first
        container.selectAll('svg').remove();

        canvas = container
          .append<SVGSVGElement>('svg')
          .attr('height', GRAPH_HEIGHT)
          .attr('width', '100%')
          .attr('class', `forecastGraphSurf-${day}`)
          .attr('data-testid', 'forecast-graph-surf-bar-container');

        // Add vertical line between graphs on tablet/desktop
        if (!isMobile && dayIndex !== 0) {
          canvas
            .append('line')
            .attr('x1', 0)
            .attr('y1', 0)
            .attr('x2', 1)
            .attr('y2', GRAPH_HEIGHT)
            .attr('class', styles.dividerLine);
        }

        // Create a clipPath to hide the rounded bottom of bars
        const defs = canvas.append('defs');
        defs
          .append('clipPath')
          .attr('id', `forecast-graph-surf-clip-path-${day}`)
          .append('rect')
          .attr('x', 0)
          .attr('y', 0)
          .attr('width', GRAPH_WIDTH)
          .attr('height', GRAPH_HEIGHT - CORNER_RADIUS) // Clip off the bottom rounded corner
          .attr('transform', `translate(0, 0)`);
      } else {
        // Update existing SVG dimensions
        canvas.attr('height', GRAPH_HEIGHT).attr('width', '100%');

        // Update existing clipPath dimensions dynamically
        canvas
          .select(`#forecast-graph-surf-clip-path-${day} rect`)
          .attr('width', GRAPH_WIDTH)
          .attr('height', GRAPH_HEIGHT - CORNER_RADIUS);
      }

      // UPDATE PATTERN: Select all existing bar groups, bind data, handle enter/update/exit
      const barGroups = canvas
        .selectAll<SVGGElement, SurfTimestep>('g.bar-group')
        .data(graphData, (_, i) => i); // key by index

      // Remove any exiting elements (if the dataset length changed)
      barGroups.exit().remove();

      // Add new elements for any new data points
      const enterGroups = barGroups
        .enter()
        .append('g')
        .attr('class', 'bar-group')
        .attr('data-testid', 'forecast-graph-surf-bar-group');

      // Add rect to new groups
      enterGroups
        .append('rect')
        .attr('width', barWidth)
        .attr('x', barPositionX)
        .attr('rx', CORNER_RADIUS)
        .attr('ry', CORNER_RADIUS)
        .attr('clip-path', `url(#forecast-graph-surf-clip-path-${day})`) // Apply clip path to hide bottom rounded corners
        .attr('class', () =>
          getBarClassNames({
            isActive: false,
            isObservedActive: isActiveHourObserved,
            isActiveBarObserved: false,
          }),
        )
        .attr('data-testid', 'forecast-graph-surf-bar');

      // Add text to new groups
      enterGroups
        .append('text')
        .attr('x', groupWidth / 2)
        .attr('y', -10)
        .attr('width', groupWidth)
        .attr('dominant-baseline', 'top')
        .attr('text-anchor', 'middle')
        .attr('class', () => styles.barLabel)
        .attr('data-testid', 'forecast-graph-surf-bar-text');

      // Update ALL groups (both existing and new)
      const allBarGroups = canvas.selectAll<SVGGElement, SurfTimestep>('g.bar-group');

      // Update positions and dimensions for ALL rects
      allBarGroups.attr(
        'transform',
        (d, index) => `translate(${xScale(index)},${barYPosition(d)})`,
      );

      // Recalculate barWidth dynamically here
      const updatedBarWidth = resize(groupWidth);

      // Update bar width attribute for all rects
      allBarGroups
        .select('rect')
        .attr('width', updatedBarWidth)
        .attr('x', groupWidth / 2 - updatedBarWidth / 2)
        .attr('height', (d) => barHeight(d) - 1)
        .attr('clip-path', `url(#forecast-graph-surf-clip-path-${day})`)
        .attr('class', (_, i) => {
          const isThisBarActive = i === activeHour;
          const isThisBarObserved = isThisBarActive && isActiveHourObserved;

          return getBarClassNames({
            isActive: isThisBarActive,
            isObservedActive: isActiveHourObserved,
            isActiveBarObserved: isThisBarObserved,
          });
        });

      // Add vertical lines above each bar
      // First, check if vertical lines already exist
      const verticalLines = allBarGroups.selectAll<SVGLineElement, SurfTimestep>('.vertical-line');

      // If they don't exist, create them
      if (verticalLines.empty()) {
        allBarGroups
          .append('line')
          .attr('class', (d, i) => {
            // Check if this is the current hour
            const isCurrentTimeHour =
              day === 0 &&
              data.observations &&
              i < data.observations.length &&
              isCurrentHour(data.observations[i].timestamp);

            return getVerticalLineClassNames({
              isActive: i === activeHour,
              isCurrentTimeHour: !!isCurrentTimeHour,
            });
          })
          .attr('x1', groupWidth / 2)
          .attr('x2', groupWidth / 2)
          .attr('y1', -GRAPH_HEIGHT) // Start from the top of the graph
          .attr('y2', -1); // Default end at the top of the bar - will be updated if observations exist
      } else {
        // Update existing vertical lines classes
        allBarGroups
          .select('.vertical-line')
          .attr('class', (d, i) => {
            // Check if this is the current hour
            const isCurrentTimeHour =
              day === 0 &&
              data.observations &&
              i < data.observations.length &&
              isCurrentHour(data.observations[i].timestamp);

            return getVerticalLineClassNames({
              isActive: i === activeHour,
              isCurrentTimeHour: !!isCurrentTimeHour,
            });
          })
          .attr('x1', groupWidth / 2)
          .attr('x2', groupWidth / 2)
          .attr('y1', -GRAPH_HEIGHT); // Start from the top of the graph
      }

      const getWaveHeightDisplay = (dataPoint: SurfTimestep, index: number) => {
        if (graphData.length === 24 && (index === 3 || index === 21)) {
          return roundSurfHeight(dataPoint?.surf?.raw.max, data.units.waveHeight);
        }
        if (graphData.length === 8 && (index === 1 || index === 6)) {
          return roundSurfHeight(dataPoint?.surf?.raw.max, data.units.waveHeight);
        }
        return null;
      };

      // Update text content
      allBarGroups.select('text').text((d, i) => getWaveHeightDisplay(d, i));

      // Handle observations if needed
      if (data.observations && day === 0) {
        // Sort observations by x value to ensure they're in order
        const sortedObservations = [...data.observations].sort((a, b) => a.timestamp - b.timestamp);

        // Prepare segments as before
        const segments: Array<Array<{ x: number; y: number; type?: string; isActive?: boolean }>> =
          [];
        let currentSegment: Array<{ x: number; y: number; type?: string; isActive?: boolean }> = [];

        sortedObservations.forEach((point, index) => {
          if (point.surf?.raw.max == null || Number.isNaN(point.surf.raw.max)) {
            if (currentSegment.length > 0) {
              segments.push([...currentSegment]);
              currentSegment = [];
            }
            return;
          }

          const pointValue = {
            x: index,
            y: point.surf.raw.max,
            type: point.surf.type as ObservationType,
            isActive: index === activeHour,
          };

          if (point.surf.type === 'HUMAN') {
            if (currentSegment.length > 0) {
              segments.push([...currentSegment]);
              currentSegment = [];
            }
            segments.push([pointValue]);
            return;
          }

          if (index === 0 || currentSegment.length === 0) {
            currentSegment.push(pointValue);
          } else {
            const prevPoint = currentSegment[currentSegment.length - 1];
            if (pointValue.x === prevPoint.x + 1) {
              currentSegment.push(pointValue);
            } else {
              segments.push([...currentSegment]);
              currentSegment = [pointValue];
            }
          }
        });

        if (currentSegment.length > 0) {
          segments.push(currentSegment);
        }

        const isObservationAtActiveHour = segments.some((segment) =>
          segment.some((point) => point.isActive),
        );

        // Flatten points for circles and vertical lines
        const observationPoints = segments.flat();

        // Function to calculate the y position for an observation point
        const getObservationYPosition = (point: { y: number }) => {
          const minWaveHeightThreshold =
            data?.units?.waveHeight?.toLowerCase() === 'm' ? 0.12 : 0.4;
          const maxHeight = point.y <= minWaveHeightThreshold ? minWaveHeightThreshold : point.y;
          return (
            GRAPH_HEIGHT -
            Math.abs(
              yAxisScaleType === 'exponent'
                ? scaleWaveHeight(
                    maxHeight,
                    data?.units?.waveHeight as SurfHeight,
                    GRAPH_HEIGHT - GRAPH_LABEL_HEIGHT,
                  )
                : yScale(maxHeight),
            )
          );
        };

        // Create a mapping of observation points by timestamp for reliable lookup
        const observationByTimestamp = new Map();
        observationPoints.forEach((point) => {
          const timestamp = data.observations?.[point.x]?.timestamp;
          if (timestamp) {
            observationByTimestamp.set(timestamp, point);
          }
        });

        // Now update the vertical lines to connect to observation points when they exist
        allBarGroups.select('.vertical-line').attr('y2', (d) => {
          const { timestamp } = d;
          const observationPoint = observationByTimestamp.get(timestamp);

          if (observationPoint) {
            // If there's an observation point, calculate distance from the bar group's origin to the observation point
            const observationY = getObservationYPosition(observationPoint);
            const barGroupY = barYPosition(d);
            // Return the relative position (vertical lines are attached to bar groups)
            return observationY - barGroupY;
          }
          // Default to top of bar
          return -1;
        });

        const simpleLine = line<{ x: number; y: number; type?: string }>()
          .x((d) => xScale(d.x) + groupWidth / 2)
          .y((d) => {
            const minWaveHeightThreshold =
              data?.units?.waveHeight?.toLowerCase() === 'm' ? 0.12 : 0.4;
            const maxHeight = d.y <= minWaveHeightThreshold ? minWaveHeightThreshold : d.y;

            return (
              GRAPH_HEIGHT -
              Math.abs(
                yAxisScaleType === 'exponent'
                  ? scaleWaveHeight(
                      maxHeight,
                      data?.units?.waveHeight as SurfHeight,
                      GRAPH_HEIGHT - GRAPH_LABEL_HEIGHT,
                    )
                  : yScale(maxHeight),
              )
            );
          });

        // Bind segments data to paths with a stable key
        const observationPaths = canvas
          .selectAll<SVGPathElement, (typeof segments)[0]>('.sl-forecast-graph-observation-path')
          .data(
            segments.filter((segment) => segment.length > 1 && segment[0].type !== 'HUMAN'),
            (segment) => segment.map((point) => point.x).join('-'), // stable unique key based on x values
          );

        observationPaths
          .enter()
          .append('path')
          .merge(observationPaths)
          .attr('d', simpleLine)
          .attr(
            'class',
            classNames(
              styles.observationPath,
              'sl-forecast-graph-observation-element',
              'sl-forecast-graph-observation-path',
              {
                [styles['observationPath--atActiveHour']]: isObservationAtActiveHour,
              },
            ),
          );

        observationPaths.exit().remove();

        // Bind data to vertical lines for HUMAN type points
        const observationPointsHuman = observationPoints.filter((p) => p.type === 'HUMAN');

        const observationPathsHuman = canvas
          .selectAll<
            SVGLineElement,
            (typeof observationPointsHuman)[0]
          >('.sl-forecast-graph-observation-path-human')
          .data(observationPointsHuman);

        observationPathsHuman
          .enter()
          .append('line')
          .merge(observationPathsHuman)
          .attr('x1', (d) => xScale(d.x) + groupWidth / 2)
          .attr('x2', (d) => xScale(d.x) + groupWidth / 2)
          .attr('y1', 20)
          .attr('y2', (d) => {
            const minWaveHeightThreshold =
              data?.units?.waveHeight?.toLowerCase() === 'm' ? 0.12 : 0.4;
            const maxHeight = d.y <= minWaveHeightThreshold ? minWaveHeightThreshold : d.y;
            return (
              GRAPH_HEIGHT -
              Math.abs(
                yAxisScaleType === 'exponent'
                  ? scaleWaveHeight(
                      maxHeight,
                      data?.units?.waveHeight as SurfHeight,
                      GRAPH_HEIGHT - GRAPH_LABEL_HEIGHT,
                    )
                  : yScale(maxHeight),
              )
            );
          })
          .attr('class', (d) =>
            classNames(
              styles.observationPathHuman,
              'sl-forecast-graph-observation-element',
              'sl-forecast-graph-observation-path-human',
              {
                [styles['observationPathHuman--active']]: d.isActive,
              },
            ),
          );

        observationPathsHuman.exit().remove();

        // Only add/remove forecaster avatars if observations have changed
        if (data.observations) {
          // Add forecaster avatars for HUMAN observations
          const forecasterAvatars = canvas
            .selectAll<SVGForeignObjectElement, (typeof observationPointsHuman)[0]>(
              `.sl-forecast-graph-forecaster-avatar-day-${day}`, // Add day to class for uniqueness
            )
            .data(
              observationPointsHuman,
              (d) => `avatar-${day}-${d.x}`, // Create a stable unique key for each avatar
            );

          // Handle entering elements - only create the HTML content once
          const enteringAvatars = forecasterAvatars
            .enter()
            .append('foreignObject')
            .attr('id', (d) => `forecaster-avatar-${day}-${d.x}`) // Add unique ID
            .attr('width', 17) // Set explicit width for iOS compatibility
            .attr('height', 17) // Set explicit height for iOS compatibility
            .attr(
              'class',
              classNames(styles.forecasterAvatar, `sl-forecast-graph-forecaster-avatar-day-${day}`),
            )
            .html((d) => {
              // Get the forecaster icon URL from the observation data
              const observation = data.observations?.[d.x];
              // Use optional chaining to safely access the forecaster property
              const iconUrl = observation?.surf?.forecaster?.iconUrl;

              if (iconUrl) {
                return `<div class="${styles.forecasterAvatarContainer}">
                      <img src="${iconUrl}" class="${styles.forecasterAvatarImage}" alt="Forecaster" />
                    </div>`;
              }
              return '';
            });

          // Only update position attributes on both new and existing elements
          // Don't regenerate the HTML content
          enteringAvatars
            .merge(forecasterAvatars)
            .attr('x', (d) => xScale(d.x) + groupWidth / 2 - 8) // Center the 16px avatar (half of 16px)
            .attr('y', 4) // Position above the line
            .attr('class', (d) =>
              classNames(
                styles.forecasterAvatar,
                `sl-forecast-graph-forecaster-avatar-day-${day}`,
                {
                  [styles['forecasterAvatar--atActiveHour']]: isObservationAtActiveHour,
                  [styles['forecasterAvatar--active']]: d.isActive,
                },
              ),
            );

          // Remove exiting elements
          forecasterAvatars.exit().remove();
        } else {
          // If there are no observations, remove any existing avatars
          canvas.selectAll(`.sl-forecast-graph-forecaster-avatar-day-${day}`).remove();
        }

        // Bind data to circles with a stable key
        const observationCircles = canvas
          .selectAll<
            SVGCircleElement,
            (typeof observationPoints)[0]
          >('.sl-forecast-graph-observation-circle')
          .data(observationPoints, (d) => d.x); // stable unique key based on x value

        observationCircles
          .enter()
          .append('circle')
          .merge(observationCircles)
          .attr('cx', (d) => xScale(d.x) + groupWidth / 2)
          .attr('cy', (d) => {
            const minWaveHeightThreshold =
              data?.units?.waveHeight?.toLowerCase() === 'm' ? 0.12 : 0.4;
            const maxHeight = d.y <= minWaveHeightThreshold ? minWaveHeightThreshold : d.y;
            return (
              GRAPH_HEIGHT -
              Math.abs(
                yAxisScaleType === 'exponent'
                  ? scaleWaveHeight(
                      maxHeight,
                      data?.units?.waveHeight as SurfHeight,
                      GRAPH_HEIGHT - GRAPH_LABEL_HEIGHT,
                    )
                  : yScale(maxHeight),
              )
            );
          })
          .attr('r', (d) => (d.type === 'HUMAN' ? 4 : 3))
          .attr('class', (d) =>
            classNames(
              styles.observationCircle,
              'sl-forecast-graph-observation-element',
              'sl-forecast-graph-observation-circle',
              {
                [styles['observationCircle--active']]: d.isActive,
                [styles['observationCircle--atActiveHour']]: isObservationAtActiveHour,
              },
            ),
          );

        observationCircles.exit().remove();

        // Handle pulse circle (only one, last point)
        const lastSegment = segments.length > 0 ? segments[segments.length - 1] : undefined;
        const lastPoint =
          lastSegment && lastSegment.length > 0 ? lastSegment[lastSegment.length - 1] : undefined;

        // Only add the pulse circle if the last point represents the current hour
        let observationCirclePulseData: Array<typeof lastPoint> = [];
        if (lastPoint && data.observations && lastPoint.x < data.observations.length) {
          if (isCurrentHour(data.observations[lastPoint.x].timestamp)) {
            observationCirclePulseData = [lastPoint];
          }
        }

        const observationCirclePulse = canvas
          .selectAll<
            SVGCircleElement,
            typeof lastPoint
          >('.sl-forecast-graph-observation-circle-pulse')
          .data(observationCirclePulseData);

        observationCirclePulse
          .enter()
          .append('circle')
          .merge(observationCirclePulse)
          .attr('cx', (d) => (d ? xScale(d.x) + groupWidth / 2 : 0))
          .attr('cy', (d) => {
            if (!d) return 0;
            const minWaveHeightThreshold =
              data?.units?.waveHeight?.toLowerCase() === 'm' ? 0.12 : 0.4;
            const maxHeight = d.y <= minWaveHeightThreshold ? minWaveHeightThreshold : d.y;
            return (
              GRAPH_HEIGHT -
              Math.abs(
                yAxisScaleType === 'exponent'
                  ? scaleWaveHeight(
                      maxHeight,
                      data?.units?.waveHeight as SurfHeight,
                      GRAPH_HEIGHT - GRAPH_LABEL_HEIGHT,
                    )
                  : yScale(maxHeight),
              )
            );
          })
          .attr(
            'class',
            classNames(
              styles.observationCirclePulse,
              'sl-forecast-graph-observation-element',
              'sl-forecast-graph-observation-circle-pulse',
            ),
          );

        observationCirclePulse.exit().remove();
      }

      graphInitializedRef.current = true;
      updateActiveBar();
    }
    // Important: don't include `updateActiveBar` here or it will cause the entire graph to re-render on every mouse move
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeHour,
    data.observations,
    data.units.waveHeight,
    data?.days,
    data?.overallMaxSurfHeight,
    day,
    dayIndex,
    graphHeight,
    graphWidth,
    isMobile,
    isActiveHourObserved,
  ]);

  useUnmount(() => {
    selectAll(`.forecastGraphSurf-${day}`).remove();
  });

  return { setupGraph };
};

export default useSetupSurfGraphCanvas;
